// path: src/components/Admin.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { VisitorsShelf } from '../palais/VisitorsShelf';
import { SignIn } from '../palais/LayoutsShelf';
import { signInWith } from '../palais/layoutsDb';
import { GoogleG } from './AccountCard';
import { useCollection, messageOf } from '../palais/useCollection';
import { denyAccount, githubStats, listAccounts, setAccepted, visitStats, type Account, type GithubStats, type VisitStats } from '../palais/visits';
import { paletteOf, useFloral } from '../palais/florals';
import '../palais/palais.css';

/**
 * mollybeach.app/admin — the visitor book, behind the same sign-in as the
 * Palais catalogue. Everything here comes from functions the database only
 * answers for editors, so a stranger who opens this page sees a sign-in form
 * and nothing else.
 *
 * It shows the same visitor book as the catalogue's ☆ Visitors page, plus the
 * numbers that don't fit in a drawer: the hours of the day people come, how
 * they're finding the site, and the GitHub profile's own counter.
 */

const RANGES = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 365, label: 'A year' },
  { days: 3650, label: 'All time' },
];

const when = (h: number) => `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`;
const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/** the hours of the day people come, as a little wall of bars */
function Hours({ hours }: { hours: { hour: number; visits: number }[] }) {
  const by = new Map(hours.map((h) => [h.hour, h.visits]));
  const most = Math.max(1, ...hours.map((h) => h.visits));
  return (
    <div className="adm-hours adm-hours--all" role="img" aria-label="Visits by hour of the day">
      {Array.from({ length: 24 }, (_, h) => {
        const n = by.get(h) ?? 0;
        return (
          <span key={h} title={`${when(h)}: ${n} visit${n === 1 ? '' : 's'}`}>
            <i style={{ height: `${Math.max(3, (n / most) * 100)}%` }} />
            <small>{when(h)}</small>
          </span>
        );
      })}
    </div>
  );
}

/** the sign-in on Molly's admin pages: her Google account (mollyjbeach@gmail.com),
    which lands straight back on the page she was on. Once signed in, it shows
    who and a way out, the same as before. */
export function AdminSignIn({ editor, onError }: { editor: ReturnType<typeof useCollection>['editor']; onError: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  if (editor) return <SignIn editor={editor} onError={onError} label="Sign in" />;
  return (
    <p className="cat-signin-line">
      <button
        type="button"
        className="cat-btn adm-google"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          onError('');
          try {
            await signInWith('google', window.location.href);
          } catch (e) {
            setBusy(false);
            onError(messageOf(e));
          }
        }}
      >
        <GoogleG className="adm-google-g" /> {busy ? 'Opening Google…' : 'Sign in with Google'}
      </button>
    </p>
  );
}

export default function Admin() {
  const collection = useCollection();
  // the same stones as the catalogue: the sidebar's for the rims, the footer's
  // for what's filled in
  const rim = paletteOf(useFloral('sidebar').now);
  const fill = paletteOf(useFloral('footer').now);
  const stones = {
    ['--cat-jewel-side' as string]: rim.jewel,
    ['--cat-jewel' as string]: fill.jewel,
    ['--cat-ink' as string]: fill.ink,
    ['--cat-ink-side' as string]: rim.ink,
  } as React.CSSProperties;
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<VisitStats | null>(null);
  const [github, setGithub] = useState<GithubStats | null>(null);
  const [error, setError] = useState('');
  const [githubError, setGithubError] = useState('');
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [accountsError, setAccountsError] = useState('');
  const [deciding, setDeciding] = useState<string | null>(null);

  const decide = async (id: string, accept: boolean) => {
    setDeciding(id);
    try {
      await setAccepted(id, accept);
      setAccounts((list) => (list ? list.map((a) => (a.id === id ? { ...a, accepted: accept } : a)) : list));
    } catch (e) {
      setAccountsError(messageOf(e));
    } finally {
      setDeciding(null);
    }
  };

  const deny = async (a: Account) => {
    const who = a.name || a.email || 'this account';
    if (!window.confirm(`Deny and permanently delete ${who}? This can\u2019t be undone.`)) return;
    setDeciding(a.id);
    try {
      await denyAccount(a.id);
      setAccounts((list) => (list ? list.filter((x) => x.id !== a.id) : list));
    } catch (e) {
      setAccountsError(messageOf(e));
    } finally {
      setDeciding(null);
    }
  };
  const signedIn = Boolean(collection.editor?.canSave);

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    visitStats(days)
      .then((s) => live && setStats(s))
      .catch((e) => live && setError(messageOf(e)));
    githubStats(days)
      .then((g) => live && setGithub(g))
      .catch((e) => live && setGithubError(messageOf(e)));
    listAccounts()
      .then((a) => live && setAccounts(a))
      .catch((e) => live && setAccountsError(messageOf(e)));
    return () => {
      live = false;
    };
  }, [days, signedIn]);

  if (!collection.configured) {
    return (
      <div className="palais adm" style={stones}>
        <h1>Visitors</h1>
        <p className="cat-note">The database isn't set up in this copy of the site (.env.local).</p>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div className="palais adm adm--gate" style={stones}>
        <div className="adm-gate">
          <h1>♡ Visitors</h1>
          <p className="cat-note">
            {collection.editor
              ? 'Signed in, but this account isn’t an editor, so the visitor book stays shut.'
              : 'Molly’s corner. Sign in to see who’s been by.'}
          </p>
          <AdminSignIn editor={collection.editor} onError={setError} />
          {error && (
            <p className="cat-error" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="palais adm" style={stones}>
      <header className="adm-head">
        <h1>♡ Visitors</h1>
        {/* over to one visitor at a time, same sign-in */}
        <Link to="/admin/profiles" className="cat-mini">
          ✦ Visitor profiles
        </Link>
        <nav className="cat-seasons" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={`cat-season${days === r.days ? ' is-on' : ''}`}
              aria-pressed={days === r.days}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </nav>
      </header>

      {error && (
        <p className="cat-error" role="alert">
          {error}
        </p>
      )}

      <div className="cat-looks vis">

        {!!stats?.by_hour?.length && (
          <section className="vis-card">
            <h3>🕰 What time they come</h3>
            <Hours hours={stats.by_hour} />
          </section>
        )}

        {!!stats?.campaigns?.length && (
          <section className="vis-card">
            <h3>🏷️ Tagged links</h3>
            <ul className="vis-list">
              {stats.campaigns.map((c, i) => (
                <li key={i}>
                  <span>
                    {c.tag ?? c.campaign ?? c.medium}
                    <small>{[c.medium, c.campaign].filter(Boolean).join(' · ') || 'no campaign'}</small>
                  </span>
                  <b>{c.visits}</b>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!!stats?.networks?.length && (
          <section className="vis-card">
            <h3>🌐 Their internet providers</h3>
            <ul className="vis-list">
              {stats.networks.map((n) => (
                <li key={n.network}>
                  <span>{n.network}</span>
                  <b>{n.unique}</b>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="vis-card">
          <h3>🐙 The GitHub profile</h3>
          {githubError ? (
            <p className="cat-note">
              {/does not exist|Could not find/i.test(githubError)
                ? 'The GitHub counter isn’t set up yet: run supabase/migrations/20260915180000_github_visits.sql in the mollybeach repo.'
                : githubError}
            </p>
          ) : !github ? (
            <p className="cat-note">Counting…</p>
          ) : (
            <>
              <div className="vis-tiles">
                <div className="vis-tile">
                  <span className="vis-num">{github.period.toLocaleString()}</span>
                  <span className="vis-label">views · {RANGES.find((r) => r.days === days)?.label}</span>
                </div>
                <div className="vis-tile">
                  <span className="vis-num">{github.today.toLocaleString()}</span>
                  <span className="vis-label">views today</span>
                </div>
                <div className="vis-tile vis-tile--soft">
                  <span className="vis-num">{github.all_time.toLocaleString()}</span>
                  <span className="vis-label">views since {day(github.first_at)}</span>
                </div>
              </div>
              {github.badges.length > 0 && (
                <ul className="vis-list">
                  {github.badges.map((b) => (
                    <li key={b.badge}>
                      <span>
                        {b.badge}
                        <small>last {new Date(b.last_at).toLocaleString()}</small>
                      </span>
                      <b>{b.views}</b>
                    </li>
                  ))}
                </ul>
              )}
              <p className="vis-hint">
                GitHub fetches README images through its own proxy, so these are views, not people: no city, no browser,
                no telling one reader from another.
              </p>
            </>
          )}
        </section>
      </div>

      {/* everyone who has signed in to the dresser (palais_users) */}
      <section className="adm-users">
        <h2>Accounts <small>{accounts ? accounts.length : ''}</small></h2>
        {accountsError ? (
          <p className="cat-note">
            {/does not exist|Could not find/i.test(accountsError)
              ? 'Run supabase/migrations/20260928130000_palais_users.sql to see who has signed in.'
              : accountsError}
          </p>
        ) : !accounts ? (
          <p className="cat-note">Reading the accounts…</p>
        ) : accounts.length === 0 ? (
          <p className="cat-note">Nobody has signed in yet.</p>
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr><th>Who</th><th>Via</th><th>Joined</th><th>Last seen</th><th>Board</th></tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id}>
                    <td className="adm-who">
                      {a.avatar && <img src={a.avatar} alt="" className="adm-avatar" referrerPolicy="no-referrer" />}
                      <span className="adm-who-text">
                        <b>{a.name || a.email || 'Someone'}</b>
                        {a.name && a.email && <small>{a.email}</small>}
                      </span>
                    </td>
                    <td><span className={`adm-via adm-via--${a.provider}`}>{a.provider}</span></td>
                    <td>{new Date(a.created_at).toLocaleDateString()}</td>
                    <td>{a.last_sign_in_at ? new Date(a.last_sign_in_at).toLocaleDateString() : '—'}</td>
                    <td className="adm-decide">
                      <span className={`adm-stat adm-stat--${a.accepted ? 'in' : 'wait'}`}>
                        {a.accepted ? 'Accepted' : 'Under review'}
                      </span>
                      {!a.accepted && (
                        <button
                          type="button"
                          className="adm-approve"
                          onClick={() => decide(a.id, true)}
                          disabled={deciding === a.id}
                        >
                          {deciding === a.id ? '…' : 'Accept'}
                        </button>
                      )}
                      {a.accepted && (
                        <button
                          type="button"
                          className="adm-approve is-in"
                          onClick={() => decide(a.id, false)}
                          disabled={deciding === a.id}
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        type="button"
                        className="adm-deny"
                        onClick={() => deny(a)}
                        disabled={deciding === a.id}
                      >
                        Deny
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* the same visitor book as the catalogue's ☆ Visitors page */}
      <VisitorsShelf forDays={days} />
    </div>
  );
}
